// A category axis spreads whatever rows it has across the whole plot, so a drilldown that returns
// one material drew a single bar floating in the middle of a 360px card, looking half-loaded. Padding
// the axis out to a minimum number of slots keeps the row pitch — and therefore the bar thickness —
// the same as the ten-row view it came from, so a short result reads as a short list and a bar does
// not change thickness halfway through a drilldown transition.
//
// The padding is invisible: each slot is a distinct run of an invisible separator, because ECharts
// needs category names to be unique but must not print them.
const PAD_CHAR = "⁣";

/** True for a slot this module added, so the caller can blank its axis label and ignore its hover. */
export function isPaddedCategory(label: string): boolean {
  return label.length > 0 && label.split("").every((character) => character === PAD_CHAR);
}

/** The labels, padded with invisible slots up to `minRows`. Unchanged when already long enough. */
export function padCategories(labels: string[], minRows?: number): string[] {
  if (!minRows || labels.length >= minRows) return labels;
  const padded = [...labels];
  while (padded.length < minRows) padded.push(PAD_CHAR.repeat(padded.length - labels.length + 1));
  return padded;
}

/**
 * A series' values, padded to match `padCategories` with nulls. ECharts draws nothing for a null,
 * and unlike 0 it is skipped by axis scaling and by the per-bar value label.
 */
export function padValues<T>(values: T[], minRows?: number): (T | null)[] {
  if (!minRows || values.length >= minRows) return values;
  return [...values, ...Array<null>(minRows - values.length).fill(null)];
}
